import { ThemeEvents } from '@theme/events';

const CART_MUTATION_PATHS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];

function isCartMutation(url) {
  try {
    const pathname = new URL(url, location.origin).pathname;
    return CART_MUTATION_PATHS.some((path) => pathname.startsWith(path));
  } catch {
    return false;
  }
}

class ShippingBar extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    this.#interceptCartRequests(signal);
    this.#fetchCartAndUpdate();

    if (this.#usesDelm) {
      document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    } else {
      this.#updateDeliveryCountdown();
      this.#startCountdownTimer();
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get #threshold() {
    return Number(this.dataset.threshold) || 0;
  }

  get #cutoffHour() {
    return Number(this.dataset.cutoffHour) || 15;
  }

  get #deliveryMin() {
    return Number(this.dataset.deliveryMin) || 3;
  }

  get #deliveryMax() {
    return Number(this.dataset.deliveryMax) || 7;
  }

  get #usesDelm() {
    return this.dataset.deliverySource === 'delm';
  }

  /**
   * Horizon swaps the hidden variant input via DOM morphing without firing a `change` event,
   * so the Delm widget never notices variant changes on its own. Push the new variant id to it.
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    const widget = this.querySelector('[data-ref="delmWidget"]');
    const variantId = event.detail?.resource?.id;
    if (!widget || !variantId) return;
    if (widget.getAttribute('data-variant-id') === String(variantId)) return;

    widget.setAttribute('data-variant-id', String(variantId));
    if (typeof widget.load === 'function') widget.load();
  };

  #handleCartUpdate = () => {
    this.#fetchCartAndUpdate();
  };

  #interceptCartRequests(signal) {
    if (ShippingBar.#intercepted) return;
    ShippingBar.#intercepted = true;

    const triggerUpdate = () => {
      document.dispatchEvent(new Event('shipping-bar:cart-change'));
    };

    // Intercept fetch
    const origFetch = window.fetch;
    window.fetch = function (...args) {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
      const result = origFetch.apply(this, args);
      if (isCartMutation(url)) {
        result.then(() => triggerUpdate()).catch(() => {});
      }
      return result;
    };

    // Intercept XMLHttpRequest
    const origXhrOpen = XMLHttpRequest.prototype.open;
    const origXhrSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.__fsbUrl = typeof url === 'string' ? url : '';
      return origXhrOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function (...args) {
      if (isCartMutation(this.__fsbUrl)) {
        this.addEventListener('load', () => triggerUpdate(), { once: true });
      }
      return origXhrSend.apply(this, args);
    };

    document.addEventListener('shipping-bar:cart-change', this.#handleCartUpdate, { signal });
  }

  static #intercepted = false;

  async #fetchCartAndUpdate() {
    try {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      });
      const cart = await response.json();
      this.#updateShipping(cart.total_price);
    } catch {
      // Silently fail
    }
  }

  #updateShipping(totalPrice) {
    const message = this.querySelector('[data-ref="message"]');
    const remaining = this.querySelector('[data-ref="remaining"]');
    const remainingAmount = this.querySelector('[data-ref="remainingAmount"]');
    const progress = this.querySelector('[data-ref="progress"]');
    const track = this.querySelector('[data-ref="track"]');
    const qualifiedMessage = this.querySelector('[data-ref="qualifiedMessage"]');
    const defaultMessage = this.querySelector('[data-ref="defaultMessage"]');

    if (!progress || !track) return;

    const threshold = this.#threshold;
    const percentage = threshold > 0 ? Math.min((totalPrice / threshold) * 100, 100) : 100;
    const isQualified = totalPrice >= threshold;

    progress.style.width = `${percentage}%`;
    track.setAttribute('aria-valuenow', String(totalPrice));

    if (isQualified) {
      if (message) message.textContent = qualifiedMessage?.content?.textContent?.trim() || '';
      if (remaining) remaining.hidden = true;
    } else {
      const diff = threshold - totalPrice;
      const formatted = this.#formatMoney(diff);

      if (message) message.textContent = defaultMessage?.content?.textContent?.trim() || '';
      if (remainingAmount) remainingAmount.textContent = formatted;
      if (remaining) remaining.hidden = false;
    }
  }

  #updateDeliveryCountdown() {
    const delivery = this.querySelector('[data-ref="delivery"]');
    if (!delivery) return;

    const countdownTemplate = this.querySelector('[data-ref="deliveryTemplate"]');
    const timeTemplate = this.querySelector('[data-ref="deliveryTimeTemplate"]');
    const countdownText = countdownTemplate?.content?.textContent?.trim() || 'Order within {time}. Estimated delivery {dates}.';
    const timeText = timeTemplate?.content?.textContent?.trim() || '{hours}h {minutes}m';

    const now = new Date();
    const cutoff = new Date(now);
    cutoff.setHours(this.#cutoffHour, 0, 0, 0);

    if (now >= cutoff) {
      cutoff.setDate(cutoff.getDate() + 1);
    }

    const diff = cutoff - now;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    const locale = document.documentElement.lang || 'en';
    const startDate = new Date(cutoff);
    startDate.setDate(startDate.getDate() + this.#deliveryMin);
    const endDate = new Date(cutoff);
    endDate.setDate(endDate.getDate() + this.#deliveryMax);

    let dates;
    try {
      dates = new Intl.DateTimeFormat(locale, { month: 'long', day: 'numeric' }).formatRange(startDate, endDate);
    } catch {
      dates = `${startDate.toLocaleDateString(locale)}–${endDate.toLocaleDateString(locale)}`;
    }

    const timeLeft = timeText.replace('{hours}', String(hours)).replace('{minutes}', String(minutes));

    delivery.innerHTML = countdownText
      .replace('{time}', `<strong>${timeLeft}</strong>`)
      .replace('{dates}', `<strong>${dates}</strong>`);
  }

  #startCountdownTimer() {
    const { signal } = this.#controller;
    const interval = setInterval(() => this.#updateDeliveryCountdown(), 60_000);
    signal.addEventListener('abort', () => clearInterval(interval));
  }

  #formatMoney(amountInCents) {
    const currency = this.dataset.currency || 'USD';
    const amount = amountInCents / 100;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      return `${amount} ${currency}`;
    }
  }
}

customElements.define('shipping-bar', ShippingBar);
